import axios from 'axios';
import { Vault } from '../lib/1895-vault-sdk';
import { VAULT_SECTIONS, SECTION_DATA_MAP, getAccessibleSections, getWritableSections } from '../constants/vaultSections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Singleton vault instance — lives for the duration of the session
let vaultInstance = null;
let securityTier = 'cloud'; // 'cloud' | 'vault'
let vaultVersion = 1; // 1 = monolithic blob, 2 = per-section encryption
let geminiApiKey = null; // Stored in memory only, never sent to 1895
let claudeApiKey = null; // Stored in memory only, never sent to 1895

// ECDH private key (encrypted) stored in localStorage for persistence across sessions
const ECDH_PRIVATE_KEY_STORAGE = '1895_ecdh_private_key';

/**
 * INITIALIZE — Call once after login.
 *
 * @param {string} password - User's vault password
 * @param {string|null} saltBase64 - From backend (null if new user)
 * @param {string} tier - 'cloud' | 'vault'
 * @param {object|null} byokConfig - { provider: 'gemini', apiKey }
 */
export async function initializeVault(password, saltBase64, tier, byokConfig) {
  securityTier = tier;

  if (tier === 'cloud') {
    vaultInstance = null;
    geminiApiKey = null;
    claudeApiKey = null;
    return;
  }

  // If no password provided, just set the tier (vault stays locked until user unlocks)
  if (!password) {
    if (byokConfig?.geminiApiKey) geminiApiKey = byokConfig.geminiApiKey;
    if (byokConfig?.claudeApiKey) claudeApiKey = byokConfig.claudeApiKey;
    // Legacy single-key support
    if (byokConfig?.apiKey && !byokConfig?.geminiApiKey) geminiApiKey = byokConfig.apiKey;
    return;
  }

  vaultInstance = new Vault();

  if (saltBase64) {
    // Existing vault — unlock and load ECDH key from localStorage
    const encryptedEcdhKey = localStorage.getItem(ECDH_PRIVATE_KEY_STORAGE);
    await vaultInstance.unlock(password, saltBase64, encryptedEcdhKey);

    // Fetch vault version from config
    try {
      const config = await getVaultConfig();
      vaultVersion = config.vault_version || 1;
    } catch { /* default to v1 */ }
  } else {
    // New vault — create with ECDH key pair
    const { salt, ecdhPublicKeyJWK, encryptedEcdhPrivateKey } = await vaultInstance.create(password);

    // Store encrypted ECDH private key in localStorage
    localStorage.setItem(ECDH_PRIVATE_KEY_STORAGE, encryptedEcdhPrivateKey);

    // Send salt + ECDH public key to backend
    await axios.post(`${API}/vault/config`, {
      salt,
      security_tier: tier,
      ecdh_public_key_jwk: JSON.stringify(ecdhPublicKeyJWK),
    });

    vaultVersion = 2;
  }

  if (byokConfig?.geminiApiKey) geminiApiKey = byokConfig.geminiApiKey;
  if (byokConfig?.claudeApiKey) claudeApiKey = byokConfig.claudeApiKey;
  // Legacy single-key support
  if (byokConfig?.apiKey && !byokConfig?.geminiApiKey) geminiApiKey = byokConfig.apiKey;
}

/**
 * SAVE PROJECT — Encrypts and saves project data as an opaque blob.
 */
export async function vaultSaveProject(projectId, projectData) {
  if (securityTier !== 'vault' || !vaultInstance) return;

  const { blob } = await vaultInstance.seal(projectData);
  await axios.post(`${API}/vault/projects/${projectId}`, { blob });
}

/**
 * SAVE PARTIAL — Encrypt and save an incremental change.
 */
export async function vaultSavePartial(projectId, dataType, data) {
  if (securityTier !== 'vault' || !vaultInstance) return;

  const { data: current } = await axios.get(`${API}/vault/projects/${projectId}`);
  if (!current.blob) return;

  const { chunk } = await vaultInstance.sealPartial(dataType, data, current.blob);
  await axios.post(`${API}/vault/projects/${projectId}/chunks`, { chunk });
}

/**
 * LOAD PROJECT — Fetches and decrypts project data.
 */
export async function vaultLoadProject(projectId) {
  if (securityTier !== 'vault' || !vaultInstance) return null;

  const { data: { blob, chunks } } = await axios.get(`${API}/vault/projects/${projectId}`);
  if (!blob) return null;

  let projectData = await vaultInstance.unseal(blob);

  if (chunks && chunks.length > 0) {
    for (const chunkBase64 of chunks) {
      const chunkData = await vaultInstance.unsealPartial(chunkBase64, blob);
      projectData = mergeChunk(projectData, chunkData);
    }
  }

  return projectData;
}

// ── V2: Per-Section Encryption ──────────────────────────────

/**
 * SAVE PROJECT V2 — Encrypt project data per-section and store.
 * Each section gets its own AES-256-GCM key.
 * Owner's section keys are wrapped with master key.
 */
export async function vaultSaveProjectV2(projectId, projectData) {
  if (securityTier !== 'vault' || !vaultInstance) return;

  // Ensure we have section keys (generate if first time)
  let sectionKeys = vaultInstance.sectionKeys;
  if (!sectionKeys || sectionKeys.size === 0) {
    // Try to load existing section keys from backend
    try {
      const { data: wrappedKeys } = await axios.get(`${API}/vault/section-keys/${projectId}`);
      if (wrappedKeys.keys && wrappedKeys.keys.length > 0) {
        if (wrappedKeys.key_type === 'master') {
          sectionKeys = await vaultInstance.unwrapSectionKeysForSelf(wrappedKeys.keys);
        } else if (wrappedKeys.key_type === 'ecdh' && wrappedKeys.owner_public_key_jwk) {
          const ownerPubJWK = JSON.parse(wrappedKeys.owner_public_key_jwk);
          sectionKeys = await vaultInstance.unwrapSectionKeys(ownerPubJWK, wrappedKeys.keys);
        }
      }
    } catch {
      // No existing keys — generate new ones
    }

    if (!sectionKeys || sectionKeys.size === 0) {
      sectionKeys = await vaultInstance.generateSectionKeys(VAULT_SECTIONS);

      // Wrap keys for self and store on backend
      const wrappedForSelf = await vaultInstance.wrapSectionKeysForSelf(sectionKeys);
      await axios.post(`${API}/vault/share-keys`, {
        project_id: projectId,
        target_user_id: null, // self
        keys: wrappedForSelf,
      });
    }
  }

  // Encrypt each section and upload
  const sectionBlobs = [];
  for (const section of VAULT_SECTIONS) {
    const fields = SECTION_DATA_MAP[section] || [];
    const sectionData = {};
    let hasData = false;
    for (const field of fields) {
      if (projectData[field] !== undefined) {
        sectionData[field] = projectData[field];
        hasData = true;
      }
    }
    if (!hasData) continue;

    const key = sectionKeys.get(section);
    if (!key) continue;

    const { blob } = await vaultInstance.sealSection(section, sectionData, key);
    sectionBlobs.push({ section, encrypted_blob: blob });
  }

  if (sectionBlobs.length > 0) {
    await axios.post(`${API}/vault/sections/${projectId}`, { sections: sectionBlobs });
  }
}

/**
 * LOAD PROJECT V2 — Fetch per-section encrypted blobs and decrypt.
 * Only decrypts sections the user has keys for (RBAC-gated).
 */
export async function vaultLoadProjectV2(projectId) {
  if (securityTier !== 'vault' || !vaultInstance) return null;

  // Load section keys
  let sectionKeys = vaultInstance.sectionKeys;
  if (!sectionKeys || sectionKeys.size === 0) {
    try {
      const { data: wrappedKeys } = await axios.get(`${API}/vault/section-keys/${projectId}`);
      if (wrappedKeys.keys && wrappedKeys.keys.length > 0) {
        if (wrappedKeys.key_type === 'master') {
          sectionKeys = await vaultInstance.unwrapSectionKeysForSelf(wrappedKeys.keys);
        } else if (wrappedKeys.key_type === 'ecdh' && wrappedKeys.owner_public_key_jwk) {
          const ownerPubJWK = JSON.parse(wrappedKeys.owner_public_key_jwk);
          sectionKeys = await vaultInstance.unwrapSectionKeys(ownerPubJWK, wrappedKeys.keys);
        }
      }
    } catch (err) {
      console.warn('Failed to load section keys:', err);
      return null;
    }
  }

  if (!sectionKeys || sectionKeys.size === 0) return null;

  // Fetch encrypted section blobs
  const { data: sectionData } = await axios.get(`${API}/vault/sections/${projectId}`);
  if (!sectionData.sections || sectionData.sections.length === 0) return null;

  // Decrypt each section we have a key for
  const projectData = {};
  for (const { section, encrypted_blob } of sectionData.sections) {
    const key = sectionKeys.get(section);
    if (!key || !encrypted_blob) continue;

    try {
      const data = await vaultInstance.unsealSection(encrypted_blob, key);
      Object.assign(projectData, data);
    } catch (err) {
      console.warn(`Failed to decrypt section "${section}":`, err.message);
    }
  }

  return Object.keys(projectData).length > 0 ? projectData : null;
}

/**
 * Share vault project keys with a collaborator.
 * Called by project owner after inviting someone.
 * @param {string} projectId
 * @param {string} collaboratorUserId
 * @param {object} permissions — collaborator's RBAC permissions
 */
export async function vaultShareProjectKeys(projectId, collaboratorUserId, permissions) {
  if (securityTier !== 'vault' || !vaultInstance) return;
  if (!vaultInstance.ecdhKeyPair) {
    console.warn('Cannot share vault keys: ECDH not initialized');
    return;
  }

  // Get collaborator's ECDH public key
  const { data: collabKeyData } = await axios.get(`${API}/vault/ecdh-pubkey/${collaboratorUserId}`);
  if (!collabKeyData.public_key_jwk) {
    console.warn('Collaborator has not enabled Vault — cannot share encrypted keys');
    return { pending: true };
  }

  const collabPubKeyJWK = JSON.parse(collabKeyData.public_key_jwk);

  // Determine which sections to share based on permissions
  const accessibleSections = getAccessibleSections(permissions);
  if (accessibleSections.length === 0) return;

  // Ensure we have section keys loaded
  let sectionKeys = vaultInstance.sectionKeys;
  if (!sectionKeys || sectionKeys.size === 0) {
    const { data: wrappedKeys } = await axios.get(`${API}/vault/section-keys/${projectId}`);
    if (wrappedKeys.keys && wrappedKeys.keys.length > 0) {
      sectionKeys = await vaultInstance.unwrapSectionKeysForSelf(wrappedKeys.keys);
    }
  }

  if (!sectionKeys || sectionKeys.size === 0) return;

  // Wrap section keys for collaborator via ECDH
  const wrappedKeys = await vaultInstance.wrapSectionKeysForCollaborator(
    collabPubKeyJWK,
    sectionKeys,
    accessibleSections
  );

  // Upload wrapped keys to backend
  await axios.post(`${API}/vault/share-keys`, {
    project_id: projectId,
    target_user_id: collaboratorUserId,
    keys: wrappedKeys,
  });

  return { shared: true, sections: accessibleSections };
}

/**
 * Update shared keys when permissions change.
 * Re-wraps keys with updated section access.
 */
export async function vaultUpdateSharedKeys(projectId, collaboratorUserId, newPermissions) {
  if (securityTier !== 'vault' || !vaultInstance) return;
  if (!vaultInstance.ecdhKeyPair) return;

  const { data: collabKeyData } = await axios.get(`${API}/vault/ecdh-pubkey/${collaboratorUserId}`);
  if (!collabKeyData.public_key_jwk) return;

  const collabPubKeyJWK = JSON.parse(collabKeyData.public_key_jwk);
  const accessibleSections = getAccessibleSections(newPermissions);

  let sectionKeys = vaultInstance.sectionKeys;
  if (!sectionKeys || sectionKeys.size === 0) {
    const { data: wrappedKeys } = await axios.get(`${API}/vault/section-keys/${projectId}`);
    if (wrappedKeys.keys?.length > 0) {
      sectionKeys = await vaultInstance.unwrapSectionKeysForSelf(wrappedKeys.keys);
    }
  }

  if (!sectionKeys || sectionKeys.size === 0) return;

  const wrappedKeys = await vaultInstance.wrapSectionKeysForCollaborator(
    collabPubKeyJWK,
    sectionKeys,
    accessibleSections
  );

  await axios.put(`${API}/vault/share-keys`, {
    project_id: projectId,
    target_user_id: collaboratorUserId,
    keys: wrappedKeys,
  });
}

/**
 * Revoke vault access for a removed member and rotate affected section keys.
 * This ensures the removed member cannot decrypt any future data even if they
 * retained their old section keys.
 *
 * @param {string} projectId
 * @param {string} removedUserId — the user being removed
 * @param {Array<{user_id, permissions}>} remainingMembers — members who keep access
 * @param {function} onProgress — optional callback for UI progress
 */
export async function vaultRevokeAccess(projectId, removedUserId, remainingMembers, onProgress) {
  if (securityTier !== 'vault' || !vaultInstance) return;

  // 1. Revoke keys on backend
  try {
    await axios.delete(`${API}/vault/section-keys/${projectId}/${removedUserId}`);
  } catch (err) {
    console.warn('Failed to revoke vault access:', err);
  }

  // 2. If vault v2 and we have ECDH, rotate keys
  if (vaultVersion >= 2 && vaultInstance.ecdhKeyPair) {
    try {
      onProgress?.('Rotating encryption keys...');

      // Load current section keys
      let sectionKeys = vaultInstance.sectionKeys;
      if (!sectionKeys || sectionKeys.size === 0) {
        const { data: wrappedKeys } = await axios.get(`${API}/vault/section-keys/${projectId}`);
        if (wrappedKeys.keys?.length > 0) {
          sectionKeys = await vaultInstance.unwrapSectionKeysForSelf(wrappedKeys.keys);
        }
      }
      if (!sectionKeys || sectionKeys.size === 0) return;

      // Generate new keys for ALL sections (full rotation)
      const newKeys = await vaultInstance.rotateSectionKeys(VAULT_SECTIONS);

      // Re-encrypt all section blobs with new keys
      const { data: sectionData } = await axios.get(`${API}/vault/sections/${projectId}`);
      if (sectionData.sections?.length > 0) {
        const reEncryptedBlobs = [];
        for (const { section, encrypted_blob } of sectionData.sections) {
          const oldKey = sectionKeys.get(section);
          const newKey = newKeys.get(section) || vaultInstance.sectionKeys.get(section);
          if (!oldKey || !newKey || !encrypted_blob) continue;

          // Decrypt with old key, re-encrypt with new key
          const data = await vaultInstance.unsealSection(encrypted_blob, oldKey);
          const { blob } = await vaultInstance.sealSection(section, data, newKey);
          reEncryptedBlobs.push({ section, encrypted_blob: blob });
        }

        if (reEncryptedBlobs.length > 0) {
          await axios.post(`${API}/vault/sections/${projectId}`, { sections: reEncryptedBlobs });
        }
      }

      // Re-wrap new keys for self (owner)
      const wrappedForSelf = await vaultInstance.wrapSectionKeysForSelf(vaultInstance.sectionKeys);
      await axios.post(`${API}/vault/share-keys`, {
        project_id: projectId,
        target_user_id: null,
        keys: wrappedForSelf,
      });

      // Re-wrap new keys for remaining members
      if (remainingMembers?.length > 0) {
        let reKeyedCount = 0;
        for (const member of remainingMembers) {
          if (!member.user_id) continue;
          try {
            const { data: collabKeyData } = await axios.get(`${API}/vault/ecdh-pubkey/${member.user_id}`);
            if (!collabKeyData.public_key_jwk) continue;

            const collabPubKeyJWK = JSON.parse(collabKeyData.public_key_jwk);
            const accessibleSections = getAccessibleSections(member.permissions || {});
            const wrappedKeys = await vaultInstance.wrapSectionKeysForCollaborator(
              collabPubKeyJWK, vaultInstance.sectionKeys, accessibleSections
            );

            await axios.put(`${API}/vault/share-keys`, {
              project_id: projectId,
              target_user_id: member.user_id,
              keys: wrappedKeys,
            });

            reKeyedCount++;
            onProgress?.(`Re-keying members... (${reKeyedCount}/${remainingMembers.length})`);
          } catch (err) {
            console.warn(`Failed to re-wrap keys for member ${member.user_id}:`, err);
          }
        }
      }

      onProgress?.('Key rotation complete');
    } catch (err) {
      console.warn('Key rotation failed:', err);
    }
  }
}

/**
 * Migrate a project from v1 (monolithic blob) to v2 (per-section encryption).
 * Called by owner when opening a v1 vault project after upgrading to v2.
 *
 * @param {string} projectId
 * @returns {{ success: boolean }}
 */
export async function migrateProjectToV2(projectId) {
  if (!vaultInstance || !vaultInstance.isUnlocked) {
    throw new Error('Vault must be unlocked to migrate');
  }

  // Fetch the v1 monolithic blob
  const { data: { blob } } = await axios.get(`${API}/vault/projects/${projectId}`);
  if (!blob) return { success: false, reason: 'No v1 blob found' };

  // Migrate: decrypt monolithic → split into sections → encrypt each
  const { sectionBlobs, sectionKeys } = await vaultInstance.migrateFromMonolithicBlob(
    blob, SECTION_DATA_MAP, VAULT_SECTIONS
  );

  // Upload section blobs
  const sections = [];
  for (const [section, blobData] of sectionBlobs) {
    sections.push({ section, encrypted_blob: blobData });
  }
  if (sections.length > 0) {
    await axios.post(`${API}/vault/sections/${projectId}`, { sections });
  }

  // Wrap and store section keys for self
  const wrappedForSelf = await vaultInstance.wrapSectionKeysForSelf(sectionKeys);
  await axios.post(`${API}/vault/share-keys`, {
    project_id: projectId,
    target_user_id: null,
    keys: wrappedForSelf,
  });

  return { success: true };
}

/** Get the current vault version */
export function getVaultVersion() {
  return vaultVersion;
}

/** Get the vault instance (for advanced operations like migration) */
export function getVaultInstance() {
  return vaultInstance;
}

/** Check if ECDH is initialized */
export function isECDHReady() {
  return !!vaultInstance?.ecdhKeyPair;
}

// ── Vault Audit Log ──────────────────────────────────────

/**
 * Log a vault audit event. Client batches and flushes.
 */
const auditBuffer = [];
let auditFlushTimer = null;

export function logVaultAudit(projectId, action, section, metadata = {}) {
  auditBuffer.push({
    project_id: projectId,
    action,
    section: section || null,
    metadata,
    timestamp: new Date().toISOString(),
  });

  // Flush every 30s or when buffer exceeds 20 events
  if (auditBuffer.length >= 20) {
    flushAuditLog();
  } else if (!auditFlushTimer) {
    auditFlushTimer = setTimeout(flushAuditLog, 30000);
  }
}

async function flushAuditLog() {
  if (auditFlushTimer) {
    clearTimeout(auditFlushTimer);
    auditFlushTimer = null;
  }
  if (auditBuffer.length === 0) return;

  const events = auditBuffer.splice(0, auditBuffer.length);
  try {
    await axios.post(`${API}/vault/audit-log`, { events });
  } catch (err) {
    // Re-add to buffer on failure
    auditBuffer.unshift(...events);
    console.warn('Failed to flush audit log:', err);
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (auditBuffer.length > 0) {
      const events = auditBuffer.splice(0, auditBuffer.length);
      const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
      navigator.sendBeacon?.(`${API}/vault/audit-log`, blob);
    }
  });
}

/**
 * Fetch audit log for a project (owner only).
 */
export async function getVaultAuditLog(projectId, limit = 100, offset = 0) {
  const { data } = await axios.get(`${API}/vault/audit-log/${projectId}`, {
    params: { limit, offset },
  });
  return data;
}

function mergeChunk(projectData, chunkData) {
  const { type, data } = chunkData;

  switch (type) {
    case 'shot': {
      if (!projectData.shots) projectData.shots = [];
      const idx = projectData.shots.findIndex(s => s.id === data.id);
      if (idx >= 0) {
        projectData.shots[idx] = data;
      } else {
        projectData.shots.push(data);
      }
      break;
    }
    case 'shot_delete':
      if (projectData.shots) {
        projectData.shots = projectData.shots.filter(s => s.id !== data.id);
      }
      break;
    case 'annotation':
      if (!projectData.annotations) projectData.annotations = [];
      projectData.annotations.push(data);
      break;
    case 'analysis_update':
      projectData.analysis = { ...projectData.analysis, ...data };
      break;
    case 'production_data':
      if (!projectData.productionData) projectData.productionData = {};
      projectData.productionData[data.dataType] = data.content;
      break;
    default:
      console.warn(`Unknown chunk type: ${type}`);
  }

  return projectData;
}

/**
 * LOCK — Call on logout. Wipes encryption keys from memory.
 * Note: ECDH private key stays in localStorage (encrypted with master key).
 */
export function lockVault() {
  if (vaultInstance) {
    vaultInstance.lock();
    vaultInstance = null;
  }
  geminiApiKey = null;
  claudeApiKey = null;
  vaultVersion = 1;
}

/** Get the current security tier */
export function getSecurityTier() {
  return securityTier;
}

/** Check if vault is unlocked */
export function isVaultUnlocked() {
  return vaultInstance?.isUnlocked || false;
}

/** Get the user's Gemini API key (for BYOK calls) */
export function getGeminiApiKey() {
  return geminiApiKey;
}

/** Get the user's Claude API key (for BYOK Greenlight analysis) */
export function getClaudeApiKey() {
  return claudeApiKey;
}

/** Get vault config for the current user from backend */
export async function getVaultConfig() {
  const { data } = await axios.get(`${API}/vault/config`);
  return data;
}

/** Update vault security tier */
export async function updateVaultConfig(tier, byokConfig) {
  await axios.put(`${API}/vault/config`, {
    security_tier: tier,
    llm_provider: byokConfig?.provider || null,
  });
  securityTier = tier;
  if (tier === 'vault') {
    if (byokConfig?.geminiApiKey) geminiApiKey = byokConfig.geminiApiKey;
    if (byokConfig?.claudeApiKey) claudeApiKey = byokConfig.claudeApiKey;
    if (byokConfig?.apiKey && !byokConfig?.geminiApiKey) geminiApiKey = byokConfig.apiKey;
  } else if (tier === 'cloud') {
    geminiApiKey = null;
    claudeApiKey = null;
  }
}
